import { useEffect, useRef, useState, useCallback } from 'react';
import appConfig from '../../app.config';

export interface VersionInfo {
  version?: string
  commitHash?: string
  buildTime?: string
}

export const useVersionCheck = () => {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState<boolean>(false);
  const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);
  const [dismissedHash, setDismissedHash] = useState<string | null>(null);
  const initialHashRef = useRef<string | null>(
    process.env.REACT_APP_COMMIT_HASH || null
  );

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch(`/version.json?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache'
        }
      });

      if (!response.ok) {
        return;
      }

      const data: VersionInfo = await response.json();
      const serverHash = data.commitHash || data.version;

      if (!serverHash) {
        return;
      }

      // If initial build hash was not known at compile time, store the first fetched hash
      if (!initialHashRef.current) {
        initialHashRef.current = serverHash;
        return;
      }

      if (initialHashRef.current !== serverHash) {
        setLatestVersion(data);
        if (dismissedHash !== serverHash) {
          setIsNewVersionAvailable(true);
        }
      }
    } catch (e) {
      // Ignore network errors during background check
    }
  }, [dismissedHash]);

  const dismissVersionPrompt = useCallback(() => {
    if (latestVersion?.commitHash || latestVersion?.version) {
      setDismissedHash(latestVersion.commitHash || latestVersion.version || null);
    }
    setIsNewVersionAvailable(false);
  }, [latestVersion]);

  useEffect(() => {
    // Initial check after a brief delay
    const initialTimer = setTimeout(() => {
      checkVersion();
    }, 5000);

    const interval = setInterval(() => {
      checkVersion();
    }, appConfig.versionCheckInterval || 60000);

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [checkVersion]);

  return {
    isNewVersionAvailable,
    latestVersion,
    dismissVersionPrompt,
    checkVersion
  };
};
